import { createFileRoute } from "@tanstack/react-router";
import { contactLink } from "@/lib/analytics";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { RelatedLinks } from "@/components/related-links";
import { ContactForm } from "@/components/contact-form";
import { Reveal, useParallax } from "@/components/reveal";
import { PhotoFigure } from "@/components/photo-figure";
import portrait from "@/assets/sylwia-portrait.jpg.asset.json";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs, breadcrumbSchema, SITE_URL, PERSON_ID } from "@/content/seo";

const title = "Kontakt | Sylwia Sitkowska";
const description =
  "Napisz do mnie w sprawie pracy indywidualnej, miejsca w mastermindzie dla liderów albo zaproszenia na wystąpienie. Odpowiadam osobiście, wszystko traktuję poufnie.";
const ogImage = abs(ogSocial);

const contactJsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: title,
  description,
  url: `${SITE_URL}/kontakt`,
  inLanguage: "pl-PL",
  mainEntity: {
    "@type": "Person",
    "@id": PERSON_ID,
    name: "Sylwia Sitkowska",
    email: "kontakt@sylwiasitkowska.pl",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Kontakt biznesowy",
      email: "kontakt@sylwiasitkowska.pl",
      availableLanguage: ["pl"],
    },
  },
};

const breadcrumbJsonLd = breadcrumbSchema([
  { name: "Strona główna", path: "/" },
  { name: "Kontakt", path: "/kontakt" },
]);

export const Route = createFileRoute("/kontakt")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sylwiasitkowska.pl/kontakt" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: "https://sylwiasitkowska.pl/kontakt" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(contactJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
    ],
  }),
  component: KontaktPage,
});

function KontaktPage() {
  const portraitRef = useParallax(16);
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="relative overflow-hidden border-b border-steel">
          <div aria-hidden="true" className="glow-coral -right-24 -top-24 h-72 w-72" />
          <div className="container-page relative grid items-center gap-10 section-pad lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <Reveal>
              <p className="eyebrow">Kontakt</p>
              <h1 className="serif-display mt-8 max-w-3xl text-[2.4rem] md:text-[3.4rem]">
                Napisz <span className="italic text-coral-text">do mnie</span>
              </h1>
              <p className="measure mt-8 border-l-2 border-coral pl-5 text-base leading-relaxed text-ink/80 md:text-lg">
                Praca indywidualna, miejsce w mastermindzie dla liderów, wystąpienie lub panel dla Twojej
                organizacji: napisz kilka zdań o sobie i sytuacji, w której jesteś. Odpowiadam
                osobiście. Wszystko, co napiszesz, traktuję poufnie.
              </p>
            </Reveal>
            <PhotoFigure
              src={portrait.url}
              alt="Sylwia Sitkowska, psycholog i psychoterapeutka"
              frameRef={portraitRef}
              imgClassName="aspect-[4/3] w-full object-cover object-[50%_20%]"
              width={1200}
              height={1500}
              priority
            />
          </div>
        </section>


        <section className="border-b border-steel bg-surface">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <p className="eyebrow">Formularz</p>
              <p className="mt-6 text-base leading-relaxed text-ink/80">
                Możesz też napisać bezpośrednio na{" "}
                <a
                  href="mailto:kontakt@sylwiasitkowska.pl"
                  {...contactLink("kontakt")}
                  className="link-underline text-ink hover:text-coral-text"
                >
                  kontakt@sylwiasitkowska.pl
                </a>
                .
              </p>
              <div className="mt-6 flex gap-5 text-xs uppercase tracking-[0.14em] text-ink-soft">
                <a
                  href="https://www.instagram.com/sitkowska.psycholog/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram Sylwii Sitkowskiej (otwiera się w nowej karcie)"
                  className="inline-flex min-h-11 items-center hover:text-coral-text"
                >
                  Instagram
                </a>
                <a
                  href="https://pl.linkedin.com/in/sylwia-sitkowska"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn Sylwii Sitkowskiej (otwiera się w nowej karcie)"
                  className="inline-flex min-h-11 items-center hover:text-coral-text"
                >
                  LinkedIn
                </a>
              </div>
            </div>

            <ContactForm />
          </div>
        </section>

        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <div className="max-w-2xl">
              <p className="eyebrow">Adres spotkań stacjonarnych</p>
              <address className="mt-6 not-italic text-base leading-relaxed text-ink/80">
                Warszawa, pl. Konstytucji 6/74
              </address>
              <p className="mt-4 text-sm text-ink-soft">
                Spotkania odbywają się wyłącznie po wcześniejszym uzgodnieniu terminu.
              </p>
            </div>
          </div>
        </section>
      </main>
      <RelatedLinks exclude={["/kontakt"]} only={["/konsultacje", "/mastermind", "/wystapienia"]} heading={"Zanim napiszesz"} />
      <SiteFooter />
    </div>
  );
}
